import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { QRCodeCanvas } from "qrcode.react";
import { ScanLine, Palette, FileText, Images, Users, Phone, Mail, ArrowUpRight, Instagram, Facebook, Music2, Linkedin, Youtube, QrCode } from "lucide-react";
import api from "../lib/api";
import Logo from "../components/Logo";
import Seo from "../components/Seo";
import { PHONE_DISPLAY, PHONE_TEL, EMAIL, EMAIL_MAILTO } from "../data/contact";

const LINKS = [
  { to: "/quote", icon: FileText, title: "Get a Free Quote", sub: "Tell us about your court — fast, no obligation", primary: true, testid: "links-quote" },
  { to: "/court-scan", icon: ScanLine, title: "Speedy Court Scan™", sub: "Upload a photo of your court for an assessment", testid: "links-scan" },
  { to: "/customiser", icon: Palette, title: "3D Court Customiser", sub: "Design your dream court in your colours", testid: "links-customiser" },
  { to: "/gallery", icon: Images, title: "The Court Archive", sub: "Real Speedy transformations, before & after", testid: "links-gallery" },
  { to: "/about", icon: Users, title: "Team Speedy", sub: "Speed Force — Resurfacing", testid: "links-about" },
];

export default function Links() {
  const [settings, setSettings] = useState(null);
  const qrRef = useRef(null);
  useEffect(() => { api.get("/settings").then((r) => setSettings(r.data)).catch(() => {}); }, []);

  const downloadQr = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return;
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = "speedy-resurfacing-links-qr.png";
    a.click();
  };

  return (
    <div className="min-h-screen bg-obsidian noise grid-lines px-5 py-12 flex flex-col items-center" data-testid="links-page">
      <Seo title="Speedy Resurfacing — Links" description="Quotes, court scans, the 3D customiser and our latest court transformations — all in one place." path="/links" />
      <div className="w-full max-w-md flex flex-col items-center">
        <Logo size="footer" />
        <p className="mt-4 font-mono text-[10px] uppercase tracking-[0.35em] text-gold text-center">
          Court Resurfacing · Custom Design · Sydney
        </p>
        <h1 className="mt-3 font-heading font-black text-3xl tracking-tighter text-center">
          YOUR COURT. <span className="text-neon">REBUILT.</span>
        </h1>

        <div className="mt-8 w-full space-y-3">
          {LINKS.map(({ to, icon: Icon, title, sub, primary, testid }) => (
            <Link key={to} to={to} data-testid={testid}
              className={`group flex items-center gap-4 rounded-sm px-5 py-4 transition-all duration-200 ${
                primary
                  ? "bg-neon text-obsidian shadow-neon hover:bg-neon-hover"
                  : "glass hover:border-neon/60"
              }`}>
              <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-sm ${primary ? "bg-obsidian/10" : "bg-white/5 text-neon"}`}>
                <Icon size={20} />
              </span>
              <span className="flex-1 min-w-0">
                <span className={`block font-heading font-bold text-base leading-tight ${primary ? "" : "text-white"}`}>{title}</span>
                <span className={`block text-xs mt-0.5 ${primary ? "text-obsidian/70" : "text-zinc-400"}`}>{sub}</span>
              </span>
              <ArrowUpRight size={16} className={`shrink-0 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 ${primary ? "text-obsidian/60" : "text-zinc-500 group-hover:text-neon"}`} />
            </Link>
          ))}

          <a href={PHONE_TEL} data-testid="links-call"
             className="group flex items-center gap-4 rounded-sm px-5 py-4 glass hover:border-gold/60 transition-all duration-200">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-white/5 text-gold"><Phone size={20} /></span>
            <span className="flex-1">
              <span className="block font-heading font-bold text-base text-white leading-tight">Call the Speedy Team</span>
              <span className="block text-xs mt-0.5 text-zinc-400">{PHONE_DISPLAY}</span>
            </span>
          </a>

          <a href={EMAIL_MAILTO} data-testid="links-email"
             className="group flex items-center gap-4 rounded-sm px-5 py-4 glass hover:border-neon/60 transition-all duration-200">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-sm bg-white/5 text-neon"><Mail size={20} /></span>
            <span className="flex-1 min-w-0">
              <span className="block font-heading font-bold text-base text-white leading-tight">Email Speedy Resurfacing</span>
              <span className="block text-xs mt-0.5 text-zinc-400 break-all">{EMAIL}</span>
            </span>
          </a>
        </div>

        <div className="mt-8 flex gap-3">
          {[{ Icon: Instagram, k: "instagram" }, { Icon: Facebook, k: "facebook" }, { Icon: Music2, k: "tiktok" }, { Icon: Linkedin, k: "linkedin" }, { Icon: Youtube, k: "youtube" }].map(({ Icon, k }) => (
            <a key={k} href={settings?.[k] || "#"} target="_blank" rel="noreferrer" data-testid={`links-social-${k}`}
               className="flex h-10 w-10 items-center justify-center rounded-sm border border-white/10 text-zinc-400 hover:border-neon hover:text-neon transition-colors">
              <Icon size={17} />
            </a>
          ))}
        </div>

        <a href={settings?.parent_url || "https://www.speedylinemarking.com.au"} target="_blank" rel="noreferrer"
           className="mt-6 inline-flex items-center gap-1 text-xs text-gold hover:text-gold-hover" data-testid="links-parent">
          A division of Speedy Line Marking <ArrowUpRight size={12} />
        </a>

        {/* QR code card — for flyers & van signage */}
        <div className="mt-8 w-full glass rounded-lg p-5 flex items-center gap-5" data-testid="links-qr-card">
          <div ref={qrRef} className="rounded-sm bg-white p-2 shrink-0">
            <QRCodeCanvas value="https://www.speedyresurfacing.com.au/links" size={200} includeMargin={false}
              bgColor="#FFFFFF" fgColor="#050505" style={{ width: 72, height: 72 }} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-sm">SCAN TO THIS PAGE</p>
            <p className="text-xs text-zinc-500 mt-0.5">Print it on flyers, quotes and the van.</p>
            <button onClick={downloadQr} data-testid="links-qr-download"
              className="mt-2.5 inline-flex items-center gap-1.5 rounded-sm border border-neon/40 px-3 py-1.5 text-xs font-bold text-neon hover:bg-neon hover:text-obsidian transition-colors">
              <QrCode size={13} /> Download QR (PNG)
            </button>
          </div>
        </div>
        <p className="mt-4 font-mono text-[10px] text-zinc-600">www.speedyresurfacing.com.au</p>
      </div>
    </div>
  );
}
